/**
 * Landing screen.
 *
 * Most customers arrive by scanning a QR that goes straight to a menu, so this
 * screen is really for the two edge cases: someone who opened the app directly
 * and needs to find the restaurant, and someone who wants to pull up an order
 * they already placed.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { normalizeScan } from '@yummy/shared';
import {
  Button,
  Callout,
  Card,
  Row,
  Screen,
  Section,
  Stack,
  Text,
  TextField,
  useT,
} from '@yummy/ui-rnr';

import { AppBar } from '../components/AppBar';

/**
 * Renders the two ways into the app when there is no QR to scan.
 *
 * @returns The landing screen.
 */
export function LandingScreen() {
  const t = useT();
  const navigate = useNavigate();
  const [restaurantCode, setRestaurantCode] = useState('');
  const [orderCode, setOrderCode] = useState('');

  const openRestaurant = () => {
    const value = normalizeScan(restaurantCode);
    if (!value) return;

    // A pasted QR URL may carry a table token rather than a slug.
    if (value.startsWith('YMT1.')) navigate(`/t/${encodeURIComponent(value)}`);
    else navigate(`/r/${encodeURIComponent(value.toLowerCase())}`);
  };

  const openOrder = () => {
    const value = orderCode.trim().toUpperCase();
    if (value.length < 4) return;
    navigate(`/order/code/${encodeURIComponent(value)}`);
  };

  const findOrderLabel = t('customer.landing.findOrder');
  const openMenuLabel = t('customer.landing.openMenu');
  const previousOrdersLabel = t('customer.landing.previousOrders');

  return (
    <Screen
      autoHideHeader
      header={
        <AppBar
          title={<Text className="text-primary text-2xl font-bold">{t('common.appName')}</Text>}
        />
      }>
      <Stack gap="xl">
        {/* The wordmark moved into the bar, so this block no longer carries it
            and no longer pads itself: it aligns with the bar's own edge. */}
        <Stack gap="sm">
          <Text className="text-2xl font-semibold">{t('customer.landing.tagline')}</Text>
          <Text className="text-muted-foreground text-base">{t('customer.landing.intro')}</Text>
        </Stack>

        <Card className="border-l-primary border-l-[3px] p-4">
          <Stack gap="md">
            <Text className="text-xl font-semibold">{t('customer.landing.howItWorks')}</Text>
            <Stack gap="sm">
              {[
                t('customer.landing.step1'),
                t('customer.landing.step2'),
                t('customer.landing.step3'),
                t('customer.landing.step4'),
              ].map((line, index) => (
                <Row key={line} gap="md" align="start">
                  <Text className="text-primary text-base font-bold">{index + 1}</Text>
                  <Text className="text-muted-foreground flex-1 text-base">{line}</Text>
                </Row>
              ))}
            </Stack>
          </Stack>
        </Card>

        <Section
          title={t('customer.landing.haveOrder')}
          subtitle={t('customer.landing.haveOrderHint')}>
          <Stack gap="md">
            <TextField
              label={t('customer.landing.orderCode')}
              value={orderCode}
              onChangeText={(value) => setOrderCode(value.toUpperCase().slice(0, 8))}
              placeholder="A3K7QM"
              autoCapitalize="characters"
              autoCorrect={false}
              maxLength={8}
              inputStyle={{ letterSpacing: 4, fontSize: 22, fontWeight: '700' }}
            />
            <Button
              onPress={openOrder}
              disabled={orderCode.trim().length < 4}
              accessibilityLabel={findOrderLabel}>
              <Text>{findOrderLabel}</Text>
            </Button>
          </Stack>
        </Section>

        <Section
          title={t('customer.landing.openedByMistake')}
          subtitle={t('customer.landing.openedByMistakeHint')}>
          <Stack gap="md">
            <TextField
              label={t('customer.landing.restaurant')}
              value={restaurantCode}
              onChangeText={setRestaurantCode}
              placeholder="smoke-test-kitchen"
              autoCapitalize="none"
              autoCorrect={false}
            />
            <Button
              variant="secondary"
              onPress={openRestaurant}
              disabled={restaurantCode.trim().length === 0}
              accessibilityLabel={openMenuLabel}>
              <Text>{openMenuLabel}</Text>
            </Button>
          </Stack>
        </Section>

        <Callout
          tone="info"
          title={t('customer.landing.pickupOnly')}
          message={t('customer.landing.pickupOnlyHint')}
          size="base"
        />

        <Button
          variant="ghost"
          onPress={() => navigate('/orders')}
          accessibilityLabel={previousOrdersLabel}>
          <Text>{previousOrdersLabel}</Text>
        </Button>
      </Stack>
    </Screen>
  );
}
